import { motion } from 'framer-motion';
import { Calendar, ChevronDown, Edit, Loader2, Plus, Save, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ConfirmModal } from '@/components/common';
import { roasterService } from '@/services';
import type {
  CreateRoasterRequest,
  Roaster,
  RoasterDay,
  RoasterEmployee,
  RoasterMonth,
} from '@/types';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

type Mode = 'view' | 'create' | 'edit';

export function RoasterPage() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [mode, setMode] = useState<Mode>('view');
  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState<RoasterMonth[]>([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [currentRoaster, setCurrentRoaster] = useState<Roaster | null>(null);
  const [enabledUsers, setEnabledUsers] = useState<RoasterEmployee[]>([]);
  const [editingRoaster, setEditingRoaster] = useState<RoasterDay[]>([]);
  const [selectedCreateMonth, setSelectedCreateMonth] = useState<{
    year: number;
    month: number;
  } | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'save' | 'update' | 'delete' | null>(null);
  const [saving, setSaving] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (mode === 'view') {
      fetchRoasterForMonth(selectedYear, selectedMonth);
    }
  }, [mode, selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // The selected month's roaster is loaded by the view-mode effect and the action handlers.
      const monthsData = await roasterService.getAvailableMonths();
      setAvailableMonths(monthsData);
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to load roaster data'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRoasterForMonth = async (year: number, month: number) => {
    try {
      const roaster = await roasterService.getRoasterForMonth(year, month);
      setCurrentRoaster(roaster);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        setCurrentRoaster(null);
      } else {
        showErrorToast(extractErrorMessage(error, 'Failed to load roaster'));
      }
    }
  };

  const handleCreateClick = async () => {
    try {
      const users = await roasterService.getEnabledUsers();
      setEnabledUsers(users);
      setMode('create');
      setEditingRoaster([]);
      setSelectedCreateMonth(null);
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to load users'));
    }
  };

  const handleEditClick = async () => {
    if (!currentRoaster) return;

    try {
      const users = await roasterService.getEnabledUsers();
      setEnabledUsers(users);
      setMode('edit');
      setEditingRoaster([...currentRoaster.roaster]);
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to load users'));
    }
  };

  const handleMonthSelect = async (year: number, month: number) => {
    setSelectedCreateMonth({ year, month });
    const daysInMonth = new Date(year, month, 0).getDate();
    const rosterDays: RoasterDay[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      rosterDays.push({
        dayNumber: day,
        day: dayName,
        employees: dayName === 'Sunday' ? [] : [...enabledUsers],
      });
    }

    setEditingRoaster(rosterDays);
  };

  const getAvailableCreateMonths = () => {
    const months: Array<{ year: number; month: number; label: string }> = [];
    const existingMonths = new Set(availableMonths.map((m) => `${m.year}-${m.month}`));

    const hasCurrentMonth = existingMonths.has(`${currentYear}-${currentMonth}`);

    if (!hasCurrentMonth) {
      for (let i = 0; i < 3; i++) {
        const date = new Date(currentYear, currentMonth - 1 + i, 1);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const key = `${year}-${month}`;

        if (!existingMonths.has(key)) {
          months.push({
            year,
            month,
            label: `${MONTHS[month - 1]} ${year}`,
          });
        }
      }
    } else {
      for (let i = 1; i <= 2; i++) {
        const date = new Date(currentYear, currentMonth - 1 + i, 1);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const key = `${year}-${month}`;

        if (!existingMonths.has(key)) {
          months.push({
            year,
            month,
            label: `${MONTHS[month - 1]} ${year}`,
          });
        }
      }
    }

    return months;
  };

  const handleAddEmployee = (dayIndex: number, employee: RoasterEmployee) => {
    const updated = [...editingRoaster];
    const day = updated[dayIndex];
    if (!day.employees.some((e) => e.employeeId === employee.employeeId)) {
      day.employees.push(employee);
      setEditingRoaster(updated);
    }
    setDropdownOpen(null);
  };

  const handleRemoveEmployee = (dayIndex: number, employeeId: number) => {
    const updated = [...editingRoaster];
    updated[dayIndex].employees = updated[dayIndex].employees.filter(
      (e) => e.employeeId !== employeeId,
    );
    setEditingRoaster(updated);
  };

  const getAvailableEmployeesForDay = (dayIndex: number): RoasterEmployee[] => {
    const day = editingRoaster[dayIndex];
    const assignedIds = new Set(day.employees.map((e) => e.employeeId));
    return enabledUsers.filter((u) => !assignedIds.has(u.employeeId));
  };

  const cleanRoasterData = (roaster: RoasterDay[]): RoasterDay[] => {
    return roaster.map((day) => ({
      dayNumber: day.dayNumber,
      day: day.day,
      employees: day.employees.map((emp) => ({
        name: emp.name,
        userId: emp.userId,
        employeeId: emp.employeeId,
      })),
    }));
  };

  const handleSave = () => {
    if (!selectedCreateMonth) return;
    setConfirmAction('save');
    setConfirmModalOpen(true);
  };

  const handleUpdate = () => {
    setConfirmAction('update');
    setConfirmModalOpen(true);
  };

  const handleDelete = () => {
    setConfirmAction('delete');
    setConfirmModalOpen(true);
  };

  const executeAction = async () => {
    if (!confirmAction) return;

    setSaving(true);
    try {
      if (confirmAction === 'save' && selectedCreateMonth) {
        const data: CreateRoasterRequest = {
          year: selectedCreateMonth.year,
          month: selectedCreateMonth.month,
          roaster: cleanRoasterData(editingRoaster),
        };
        await roasterService.createRoaster(data);
        showSuccessToast('Roaster created successfully');
        setMode('view');
        setSelectedYear(selectedCreateMonth.year);
        setSelectedMonth(selectedCreateMonth.month);
        await fetchData();
        await fetchRoasterForMonth(selectedCreateMonth.year, selectedCreateMonth.month);
      } else if (confirmAction === 'update' && currentRoaster) {
        const data: CreateRoasterRequest = {
          year: currentRoaster.year,
          month: currentRoaster.month,
          roaster: cleanRoasterData(editingRoaster),
        };
        await roasterService.updateRoaster(currentRoaster.year, currentRoaster.month, data);
        showSuccessToast('Roaster updated successfully');
        setMode('view');
        await fetchData();
        await fetchRoasterForMonth(currentRoaster.year, currentRoaster.month);
      } else if (confirmAction === 'delete' && currentRoaster) {
        const deletedYear = currentRoaster.year;
        const deletedMonth = currentRoaster.month;
        await roasterService.deleteRoaster(deletedYear, deletedMonth);
        showSuccessToast('Roaster deleted successfully');
        await fetchData();
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        setSelectedYear(currentYear);
        setSelectedMonth(currentMonth);
        await fetchRoasterForMonth(currentYear, currentMonth);
      }
      setConfirmModalOpen(false);
      setConfirmAction(null);
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to perform action'));
    } finally {
      setSaving(false);
    }
  };

  const canEdit = useMemo(() => {
    if (!currentRoaster) return false;
    const roasterDate = new Date(currentRoaster.year, currentRoaster.month - 1, 1);
    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), 1);
    return roasterDate >= currentDate;
  }, [currentRoaster]);

  const monthOptions = useMemo(() => {
    const options: Array<{ year: number; month: number; label: string }> = [];
    const allMonths = new Set(availableMonths.map((m) => `${m.year}-${m.month}`));
    const currentMonthKey = `${currentYear}-${currentMonth}`;
    const currentMonthIncluded = allMonths.has(currentMonthKey);

    for (let i = -12; i <= 12; i++) {
      const date = new Date(currentYear, currentMonth - 1 - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const key = `${year}-${month}`;

      if (allMonths.has(key) || (i === 0 && !currentMonthIncluded)) {
        options.push({
          year,
          month,
          label: `${MONTHS[month - 1]} ${year}`,
        });
      }
    }

    return options.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [availableMonths, currentYear, currentMonth]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 text-neolab-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Roaster Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-xs font-medium">
            Manage employee work schedules and assignments.
          </p>
        </motion.div>

        {mode === 'view' && (
          <div className="flex gap-2">
            {canEdit && currentRoaster && (
              <>
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-neolab-green text-white font-black text-xs hover:bg-neolab-green/90 transition-all cursor-pointer"
                >
                  <Edit className="w-3.5 h-3.5" />
                  Edit Roaster
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500 text-white font-black text-xs hover:bg-rose-600 transition-all cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </>
            )}
            <button
              type="button"
              onClick={handleCreateClick}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-brand text-white font-black text-xs hover:opacity-90 transition-all cursor-pointer shadow-lg shadow-green-500/20"
            >
              <Plus className="w-3.5 h-3.5" />
              Create New
            </button>
          </div>
        )}

        {mode !== 'view' && (
          <button
            type="button"
            onClick={() => {
              setMode('view');
              setEditingRoaster([]);
              setSelectedCreateMonth(null);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-black text-xs hover:opacity-90 transition-all cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
        )}
      </div>

      {mode === 'view' && (
        <>
          <div className="flex items-center gap-4">
            <div className="relative">
              <select
                value={`${selectedYear}-${selectedMonth}`}
                onChange={(e) => {
                  const [year, month] = e.target.value.split('-').map(Number);
                  setSelectedYear(year);
                  setSelectedMonth(month);
                }}
                className="appearance-none bg-white dark:bg-neolab-forest border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 pr-10 text-sm font-bold text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-neolab-green"
              >
                {monthOptions.map((opt) => (
                  <option key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {currentRoaster ? (
            <div className="bg-white dark:bg-neolab-forest rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="mb-4">
                <h2 className="text-xl font-black text-slate-900 dark:text-white">
                  {MONTHS[selectedMonth - 1]} {selectedYear} Roaster
                </h2>
              </div>
              <div className="space-y-2">
                {currentRoaster.roaster.map((day) => (
                  <div
                    key={day.dayNumber}
                    className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="w-28 text-sm font-black text-slate-900 dark:text-white">
                      {day.dayNumber}. {day.day}
                    </div>
                    <div className="flex-1 flex flex-wrap gap-1.5">
                      {day.employees.length > 0 ? (
                        day.employees.map((emp) => (
                          <span
                            key={emp.employeeId}
                            className="px-2.5 py-1 rounded-lg bg-neolab-green/10 text-neolab-green font-bold text-xs"
                          >
                            {emp.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-400 text-xs">No employees assigned</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-neolab-forest rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-bold text-sm">No roaster found for this month</p>
            </div>
          )}
        </>
      )}

      {(mode === 'create' || mode === 'edit') && (
        <div className="bg-white dark:bg-neolab-forest rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          {mode === 'create' && !selectedCreateMonth && (
            <div className="mb-4">
              <h2 className="text-lg font-black text-slate-900 dark:text-white mb-3">
                Select Month to Create Roaster
              </h2>
              <div className="flex flex-wrap gap-2">
                {getAvailableCreateMonths().map((month) => (
                  <button
                    key={`${month.year}-${month.month}`}
                    type="button"
                    onClick={() => handleMonthSelect(month.year, month.month)}
                    className="px-4 py-2 rounded-xl bg-gradient-brand text-white font-black text-xs hover:opacity-90 transition-all cursor-pointer shadow-lg shadow-green-500/20"
                  >
                    {month.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {((mode === 'create' && selectedCreateMonth) || mode === 'edit') && (
            <>
              <div className="mb-4">
                <h2 className="text-xl font-black text-slate-900 dark:text-white">
                  {mode === 'create'
                    ? `${MONTHS[selectedCreateMonth!.month - 1]} ${selectedCreateMonth!.year} Roaster`
                    : `${MONTHS[selectedMonth - 1]} ${selectedYear} Roaster`}
                </h2>
              </div>

              <div className="space-y-2">
                {editingRoaster.map((day, dayIndex) => {
                  const availableEmployees = getAvailableEmployeesForDay(dayIndex);
                  const showAddButton = availableEmployees.length > 0 && day.day !== 'Sunday';

                  return (
                    <div
                      key={day.dayNumber}
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                    >
                      <div className="w-28 text-sm font-black text-slate-900 dark:text-white">
                        {day.dayNumber}. {day.day}
                      </div>
                      <div className="flex-1 flex flex-wrap gap-1.5">
                        {day.employees.map((emp) => (
                          <span
                            key={emp.employeeId}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-neolab-green/10 text-neolab-green font-bold text-xs"
                          >
                            {emp.name}
                            <button
                              type="button"
                              onClick={() => handleRemoveEmployee(dayIndex, emp.employeeId)}
                              className="hover:bg-neolab-green/20 rounded p-0.5 transition-colors cursor-pointer"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        ))}
                        {showAddButton && (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                setDropdownOpen(dropdownOpen === dayIndex ? null : dayIndex)
                              }
                              className="flex items-center justify-center w-7 h-7 rounded-lg bg-neolab-green text-white hover:bg-neolab-green/90 transition-all cursor-pointer"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                            {dropdownOpen === dayIndex && (
                              <div className="absolute top-full left-0 mt-1.5 w-56 max-h-52 overflow-y-auto bg-white dark:bg-neolab-forest border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-10">
                                {availableEmployees.map((emp) => (
                                  <button
                                    key={emp.employeeId}
                                    type="button"
                                    onClick={() => handleAddEmployee(dayIndex, emp)}
                                    className="w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-white font-medium text-xs cursor-pointer"
                                  >
                                    {emp.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex gap-2 justify-end">
                {mode === 'edit' && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500 text-white font-black text-xs hover:bg-rose-600 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  onClick={mode === 'create' ? handleSave : handleUpdate}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-brand text-white font-black text-xs hover:opacity-90 transition-all disabled:opacity-50 shadow-lg shadow-green-500/20 cursor-pointer"
                >
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  {mode === 'create' ? 'Save' : 'Update'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setConfirmAction(null);
        }}
        onConfirm={executeAction}
        title={
          confirmAction === 'save'
            ? 'Create Roaster'
            : confirmAction === 'update'
              ? 'Update Roaster'
              : 'Delete Roaster'
        }
        message={
          confirmAction === 'save'
            ? 'Are you sure you want to create this roaster?'
            : confirmAction === 'update'
              ? 'Are you sure you want to update this roaster?'
              : 'Are you sure you want to delete this roaster? This action cannot be undone.'
        }
        confirmLabel={confirmAction === 'delete' ? 'Delete' : 'Confirm'}
        variant={confirmAction === 'delete' ? 'danger' : 'success'}
        isLoading={saving}
      />
    </div>
  );
}
